import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp } from 'lucide-react';
import { useRef, useState } from 'react';

import { definirPadraoDasPecas, type Escritorio } from '@/api/client';
import { ajustarProntoDoEscritorio, fetchEstilosDoEscritorio } from '@/api/editor';
import { fetchTimbradoDe, saveTimbradoPagina, type AlvoDoTimbrado } from '@/api/timbrado';
import {
  DOCUMENTO_PADRAO,
  PainelFormatacao,
  diferencaDeFormato,
  formatoDoPronto,
  paginaDoDocumento,
  timbradoDoArquivo,
  type ExtracaoDeArquivo,
  type PaginaPadrao,
} from '@/components/editor-forense';
import { EditorDePagina } from '@/components/pecas/EditorDePagina';
import { EditorDoCorpo } from '@/components/pecas/EditorDoCorpo';
import { FormularioDoTimbrado } from '@/components/pecas/FormularioDoTimbrado';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { extrairDeArquivo } from '@/lib/editor/formatacaoArquivo';
import { chaveDoTimbrado, gravarTimbrado, timbradoDoServidor } from '@/lib/editor/timbradoEditor';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

const REGRAS = [
  {
    valor: 'obrigatorio',
    titulo: 'Obrigatório',
    ajuda: 'O timbrado, a página e o Corpo da peça do escritório valem para todos, por cima dos de cada um.',
  },
  {
    valor: 'sugerido',
    titulo: 'Sugerido',
    ajuda: 'Quem tem os seus usa os seus; os do escritório valem para quem não tem.',
  },
] as const;

/**
 * A aba "Peças" do escritório (docs/PLANO_FORMATACAO_DO_ARQUIVO.md, Bloco F): o padrão
 * das peças dele — a regra (obrigatório | sugerido), o papel timbrado, a página das
 * peças novas e o Corpo da peça. Os mesmos componentes do timbrado pessoal, sobre o
 * escritório. Todo membro vê; quem o administra (proprietário e sócio) muda.
 */
export function PecasAba({ escritorio, podeEditar, variosEscritorios }: {
  escritorio: Escritorio;
  podeEditar: boolean;
  /** Quem está em mais de um escritório: o padrão vale para quem tem este como principal. */
  variosEscritorios: boolean;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useNotification();
  const alvo: AlvoDoTimbrado = { escritorio: escritorio.id };
  const obrigatorio = escritorio.padrao_obrigatorio !== false;
  const [regra, setRegra] = useState<'obrigatorio' | 'sugerido'>(obrigatorio ? 'obrigatorio' : 'sugerido');
  // Depois de importar de uma petição, os formulários renascem do que foi gravado.
  const [versao, setVersao] = useState(0);
  const [importando, setImportando] = useState(false);
  // O que a importação já gravou: se um passo falhar, a pessoa sabe o que entrou.
  const feitosRef = useRef<string[]>([]);

  const timbrado = useQuery({ queryKey: chaveDoTimbrado(alvo), queryFn: () => fetchTimbradoDe(alvo) });
  const estilos = useQuery({
    queryKey: queryKeys.editor.estilosDoEscritorio(escritorio.id),
    queryFn: () => fetchEstilosDoEscritorio(escritorio.id),
  });

  const salvarRegra = useMutation({
    mutationFn: (ob: boolean) => definirPadraoDasPecas(escritorio.id, ob),
    onSuccess: (salvo) => {
      queryClient.setQueryData(queryKeys.escritorios.detail(escritorio.id), salvo);
      // O editor lê a regra nos estilos e no timbrado resolvido.
      void queryClient.invalidateQueries({ queryKey: queryKeys.editor.estilos });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.timbrado.resolvido] });
      showSuccess(salvo.padrao_obrigatorio ? 'O padrão do escritório vale para todos.' : 'O padrão do escritório agora é sugerido.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  /** A petição modelo vira, de uma vez, o Corpo da peça, a página e o timbrado do escritório. */
  const importar = useMutation({
    mutationFn: async ({ x, usarTimbrado }: { x: ExtracaoDeArquivo; usarTimbrado: boolean }) => {
      const feitos: string[] = [];
      feitosRef.current = feitos;
      // O de agora, não o do cache: o editor (ou outro sócio) pode tê-los mudado.
      const [corpoAtual, timbradoAtual] = await Promise.all([
        fetchEstilosDoEscritorio(escritorio.id).then((e) => e.ajustesProntos?.corpo ?? {}),
        fetchTimbradoDe(alvo),
      ]);
      queryClient.setQueryData(chaveDoTimbrado(alvo), timbradoAtual);
      if (Object.keys(x.formato).length) {
        const base = formatoDoPronto('corpo', false);
        const ajustes = diferencaDeFormato(base, { ...base, ...corpoAtual, ...x.formato });
        await ajustarProntoDoEscritorio(escritorio.id, 'corpo', Object.keys(ajustes).length ? ajustes : null);
        feitos.push('o Corpo da peça');
      }
      const { margens, papel, orientacao } = x.documento;
      if (papel || orientacao || (margens && Object.keys(margens).length)) {
        const atual: PaginaPadrao = timbradoAtual.pagina ?? paginaDoDocumento(DOCUMENTO_PADRAO);
        await saveTimbradoPagina({
          papel: papel ?? atual.papel,
          orientacao: orientacao ?? atual.orientacao,
          margens: { ...atual.margens, ...(margens ?? {}) },
          nomeMargens: margens && Object.keys(margens).length ? 'personalizadas' : atual.nomeMargens,
        }, alvo);
        feitos.push('a página');
      }
      if (usarTimbrado) {
        const novo = timbradoDoArquivo(x, timbradoDoServidor(timbradoAtual), 'jurisapp');
        if (novo) {
          await gravarTimbrado(queryClient, novo, { alvo });
          feitos.push('o timbrado');
        }
      }
      return feitos;
    },
    onSuccess: (feitos) => {
      showSuccess(feitos.length ? `Da petição: ${feitos.join(', ')} do escritório.` : 'Nada a usar desta petição.');
    },
    onError: (err) => {
      notifyMutationError(err, showError);
      const feitos = feitosRef.current;
      if (feitos.length) showInfo(`Já entraram ${feitos.join(' e ')}; o resto não.`);
    },
    // Dê certo ou não, os formulários renascem do que o servidor tem agora.
    onSettled: async () => {
      setImportando(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chaveDoTimbrado(alvo) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.editor.estilos }),
        queryClient.invalidateQueries({ queryKey: [...queryKeys.timbrado.resolvido] }),
      ]);
      setVersao((v) => v + 1);
    },
  });

  return (
    <div className="space-y-4">
      {variosEscritorios && (
        <Alert>
          <AlertDescription>
            O padrão vale para quem tem este como escritório principal (o primeiro em que entrou).
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="text-base">Padrão das peças</CardTitle>
            <CardDescription>As peças já escritas não mudam: o padrão vale para as próximas.</CardDescription>
          </div>
          {podeEditar && (
            <Button variant="outline" onClick={() => setImportando(true)} disabled={importar.isPending || !timbrado.data || !estilos.data}>
              <FileUp className="mr-2 h-4 w-4" />
              Importar de uma petição
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <RadioGroup
            value={regra}
            onValueChange={(v) => setRegra(v as 'obrigatorio' | 'sugerido')}
            aria-label="Padrão das peças"
            className="gap-3"
            disabled={!podeEditar}
          >
            {REGRAS.map((o) => (
              <Label
                key={o.valor}
                htmlFor={`padrao-pecas-${o.valor}`}
                className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal has-[[data-state=checked]]:border-primary"
              >
                <RadioGroupItem id={`padrao-pecas-${o.valor}`} value={o.valor} className="mt-0.5" />
                <span>
                  <span className="block font-medium">{o.titulo}</span>
                  <span className="block text-xs text-muted-foreground">{o.ajuda}</span>
                </span>
              </Label>
            ))}
          </RadioGroup>
          {podeEditar && (regra === 'obrigatorio') !== obrigatorio && (
            <div className="flex justify-end">
              <Button onClick={() => salvarRegra.mutate(regra === 'obrigatorio')} disabled={salvarRegra.isPending}>
                {salvarRegra.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
                Salvar a regra
              </Button>
            </div>
          )}
          {!podeEditar && (
            <p className="text-xs text-muted-foreground">Quem administra o escritório (proprietário e sócios) muda o padrão das peças.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Papel timbrado</CardTitle>
        </CardHeader>
        <CardContent>
          <FormularioDoTimbrado key={versao} alvo={alvo} podeEditar={podeEditar} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Página das peças novas</CardTitle>
        </CardHeader>
        <CardContent>
          <EditorDePagina key={versao} alvo={alvo} podeEditar={podeEditar} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Corpo da peça</CardTitle>
          <CardDescription>A fonte e o parágrafo do texto. Os parágrafos nele mudam juntos.</CardDescription>
        </CardHeader>
        <CardContent>
          <EditorDoCorpo key={versao} escritorioId={escritorio.id} />
        </CardContent>
      </Card>

      {podeEditar && (
        <PainelFormatacao
          aberto={importando}
          extrair={extrairDeArquivo}
          alcances={[]}
          doEscritorio
          timbrado="proprio"
          timbradoAtual={timbrado.data ? timbradoDoServidor(timbrado.data) : null}
          margens={timbrado.data?.pagina?.margens ?? DOCUMENTO_PADRAO.margens}
          numerada
          aoFechar={() => setImportando(false)}
          aoAplicar={(x, { usarTimbrado }) => importar.mutate({ x, usarTimbrado })}
        />
      )}
    </div>
  );
}
